import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useRoles } from '../context/RolesContext';
import './Study.css';

import SelectDomain from './StudyPageComponents/SelectDomain';
import SelectGroup from './StudyPageComponents/SelectGroup';
import SelectTopic from './StudyPageComponents/SelectTopic';
import QuestionsList from './StudyPageComponents/QuestionsList';
import AddGoal from '../components/AddGoal';
import NewTopicModal from './StudyPageComponents/NewTopicModal';
import TopicSearch from './StudyPageComponents/TopicSearch';

function Study() {
  const { authFetch } = useAuthFetch();
  const { isAdmin } = useRoles();

  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(
    () => localStorage.getItem('studySelectedDomain') || ''
  );
  const [groups, setGroups] = useState([]);
  const groupsByDomainRef = useRef({});
  const [selectedGroup, setSelectedGroup] = useState(
    () => localStorage.getItem('studySelectedGroup') || ''
  );
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(
    () => localStorage.getItem('studySelectedTopic') || ''
  );
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [newDomainName, setNewDomainName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newTopicName, setNewTopicName] = useState('');
  const [addingTopic, setAddingTopic] = useState(false);
  const [similarTopicsWarning, setSimilarTopicsWarning] = useState(null); // array of similar topics or null
  const [pendingTopicPayload, setPendingTopicPayload] = useState(null);   // saved payload for force retry
  const [exactMatchWarning, setExactMatchWarning] = useState(false);      // true = exact match, hide Create Anyway
  const [newTopicModal, setNewTopicModal] = useState(null);               // { topic, newId } after creation
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [wrongGroupWarning, setWrongGroupWarning] = useState(null);       // { suggestedDomainId, suggestedDomainName, suggestedGroupId, suggestedGroupName, topicName }
  const [goalDate, setGoalDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('studySidebarCollapsed') === 'true'
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('studySidebarCollapsed', String(!prev));
      return !prev;
    });
  };

  // Load domains (with groups nested) on mount
  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/domains/with-groups`)
      .then((res) => {
        if (!res.ok) return res.json().then((body) => { throw new Error(`HTTP ${res.status}: ${body.detail || body.error}`); });
        return res.json();
      })
      .then((data) => {
        setDomains(data);
        const byDomain = Object.fromEntries(data.map(d => [String(d.id), d.groups]));
        groupsByDomainRef.current = byDomain;
        // If a domain was already restored from localStorage, hydrate groups immediately
        if (selectedDomain && selectedDomain !== 'all') {
          const groups = byDomain[selectedDomain] || [];
          setGroups(groups);
          const savedGroup = localStorage.getItem('studySelectedGroup');
          if (savedGroup && groups.some(g => String(g.id) === savedGroup)) {
            setSelectedGroup(savedGroup);
          }
        }
      })
      .catch((err) => console.error('Error fetching domains:', err.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isRandomMode = selectedDomain === 'all';

  // Handle notification click: ?domain=X&group=Y&topic=Z navigates the sidebar
  useEffect(() => {
    if (!domains.length) return;
    const params = new URLSearchParams(window.location.search);
    const domain = params.get('domain');
    const group = params.get('group');
    const topic = params.get('topic');
    if (domain && group && topic) {
      window.history.replaceState({}, '', '/study');
      handleSearchNavigate({ domain_id: domain, group_id: group, id: topic });
    }
  }, [domains]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate groups from preloaded data when domain changes
  useEffect(() => {
    if (selectedDomain && selectedDomain !== 'all') {
      const data = groupsByDomainRef.current[selectedDomain] || [];
      setGroups(data);
      const savedGroup = localStorage.getItem('studySelectedGroup');
      if (savedGroup && data.some((g) => String(g.id) === savedGroup)) {
        setSelectedGroup(savedGroup);
      }
    }
  }, [selectedDomain]);

  // Load random questions when Study Everything is selected
  useEffect(() => {
    if (selectedDomain !== 'all') return;
    authFetch(`${process.env.REACT_APP_API_URL}/api/questions/random`)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then((data) => setQuestions(data))
      .catch((err) => console.error('Error fetching random questions:', err));
  }, [selectedDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load topics when group changes
  useEffect(() => {
    if (selectedGroup) {
      authFetch(`${process.env.REACT_APP_API_URL}/api/topics/${selectedGroup}`)
        .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
        .then((data) => {
          setTopics(data);
          setTopicsLoading(false);
          const savedTopic = localStorage.getItem('studySelectedTopic');
          if (savedTopic && data.some((t) => String(t.id) === savedTopic)) {
            setSelectedTopic(savedTopic);
          }
        })
        .catch((err) => { console.error('Error fetching topics:', err); setTopicsLoading(false); });
    }
  }, [selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load questions and goal when topic changes
  useEffect(() => {
    setGoalDate('');
    setStartDate('');
    setExtendOpen(false);
    setExtendDate('');
    setQuestions([]);
    if (!selectedTopic) return;

    setQuestionsLoading(true);

    authFetch(`${process.env.REACT_APP_API_URL}/api/questions/${selectedTopic}/with-goal`)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(({ questions, goal }) => {
        setQuestions(questions);
        setGoalDate(goal.goal_date ? goal.goal_date.split('T')[0] : '');
        setStartDate(goal.start_date ? goal.start_date.split('T')[0] : '');
      })
      .catch((err) => console.error('Error fetching topic data:', err))
      .finally(() => setQuestionsLoading(false));
  }, [selectedTopic]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDomainChange = (e) => {
    const val = e.target.value;
    setSelectedDomain(val);
    setGroups([]);
    setSelectedGroup('');
    setTopics([]);
    setTopicsLoading(false);
    setSelectedTopic('');
    setQuestions([]);
    localStorage.setItem('studySelectedDomain', val);
    localStorage.removeItem('studySelectedGroup');
    localStorage.removeItem('studySelectedTopic');
  };

  const handleGroupChange = (e) => {
    const val = e.target.value;
    setSelectedGroup(val);
    setTopics([]);
    setTopicsLoading(!!val);
    setSelectedTopic('');
    setQuestions([]);
    localStorage.setItem('studySelectedGroup', val);
    localStorage.removeItem('studySelectedTopic');
  };

  const handleTopicChange = (e) => {
    const val = e.target.value;
    setSelectedTopic(val);
    localStorage.setItem('studySelectedTopic', val);
    setSidebarOpen(false);
  };

  const handleGenerateQuestions = async () => {
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/questions/chatgpt/generate/${selectedTopic}`, { signal: controller.signal });
      if (res.status === 429) {
        const data = await res.json();
        alert(data.error || "You've reached your daily question generation limit. Try again tomorrow.");
        return;
      }
      // 503 = the site-wide pause switch (backend QUESTION_GENERATION_PAUSED), not
      // a failure and not this user's limit. Without this branch it falls into the
      // generic catch below and tells them to "try again", which never works.
      if (res.status === 503) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'New question generation is paused right now.');
        return;
      }
      if (!res.ok) throw new Error(`Server returned status: ${res.status}`);
      await res.json();
      const qRes = await authFetch(`${process.env.REACT_APP_API_URL}/api/questions/${selectedTopic}`);
      if (qRes.ok) setQuestions(await qRes.json());
    } catch (err) {
      if (err.name === 'AbortError') alert('Request timed out after 90 seconds. Please try again.');
      else { console.error('Error generating question:', err); alert('Failed to generate question. Please try again.'); }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleBatchGenerate = async () => {
    setBatchGenerating(true);
    setBatchProgress(0);
    let successCount = 0;
    let pausedMessage = null;
    for (let i = 0; i < 5; i++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 150000);
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/questions/chatgpt/generate/${selectedTopic}`, { signal: controller.signal });
        // Site-wide pause: stop after the first 503 rather than firing the
        // remaining attempts at a switch that is off, and say why. Otherwise this
        // loop hammers the endpoint five times and reports "Generated 0/5".
        if (res.status === 503) {
          const data = await res.json().catch(() => ({}));
          pausedMessage = data.error || 'New question generation is paused right now.';
          break;
        }
        if (res.ok) {
          const data = await res.json();
          if (!data.error) successCount++;
        }
      } catch (err) {
        // skip individual failures (timeout, network error)
        console.error(`Batch generate attempt ${i + 1} failed:`, err);
      } finally {
        clearTimeout(timeoutId);
      }
      setBatchProgress(i + 1);
    }
    try {
      const qRes = await authFetch(`${process.env.REACT_APP_API_URL}/api/questions/${selectedTopic}`);
      if (qRes.ok) setQuestions(await qRes.json());
    } catch (err) {
      console.error('Error refreshing questions after batch generate:', err);
    }
    setBatchGenerating(false);
    setBatchProgress(0);
    alert(pausedMessage || `Generated ${successCount}/5 questions`);
  };

  const handleAddNewDomain = async () => {
    if (!newDomainName.trim()) return;
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domainName: newDomainName }),
      });
      const data = await response.json();
      if (data.error) {
        alert(`Error: ${data.error}`);
      } else {
        alert(`Domain added: ${data.newDomain.name}`);
        setNewDomainName('');
        authFetch(`${process.env.REACT_APP_API_URL}/api/domains`)
          .then((res) => res.json())
          .then((data) => setDomains(data))
          .catch((err) => console.error('Error refreshing domains:', err));
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      alert('Something went wrong adding the domain.');
    }
  };

  const handleAddNewGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupName: newGroupName, domainId: selectedDomain }),
      });
      const data = await response.json();
      if (data.error) {
        alert(`Error: ${data.error}`);
      } else {
        alert(`Group added: ${data.newGroup.name}`);
        setNewGroupName('');
        authFetch(`${process.env.REACT_APP_API_URL}/api/groups/${selectedDomain}`)
          .then((res) => res.json())
          .then((data) => setGroups(data))
          .catch((err) => console.error('Error refreshing groups:', err));
      }
    } catch (error) {
      console.error('Error adding group:', error);
      alert('Something went wrong adding the group.');
    }
  };

  const handleAddNewTopic = async () => {
    if (!newTopicName.trim()) return;
    setAddingTopic(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/topics/chatgpt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topicName: newTopicName,
          groupId: selectedGroup,
          groupName: groups.find((g) => g.id === parseInt(selectedGroup, 10))?.name,
          domainName: domains.find((d) => d.id === parseInt(selectedDomain, 10))?.name,
        }),
        signal: controller.signal,
      });
      const data = await response.json();
      if (response.status === 409 && data.warning === 'exact_match') {
        setExactMatchWarning(true);
        setSimilarTopicsWarning([data.topic]);
      } else if (response.status === 409 && data.warning === 'similar_topics') {
        setExactMatchWarning(false);
        setPendingTopicPayload({
          topicName: newTopicName,
          groupId: selectedGroup,
          groupName: groups.find((g) => g.id === parseInt(selectedGroup, 10))?.name,
          domainName: domains.find((d) => d.id === parseInt(selectedDomain, 10))?.name,
        });
        setSimilarTopicsWarning(data.topics);
      } else if (response.status === 409 && data.warning === 'wrong_group') {
        setPendingTopicPayload({
          topicName: newTopicName,
          groupId: selectedGroup,
          groupName: groups.find((g) => g.id === parseInt(selectedGroup, 10))?.name,
          domainName: domains.find((d) => d.id === parseInt(selectedDomain, 10))?.name,
        });
        setWrongGroupWarning({
          suggestedDomainId: data.suggestedDomainId,
          suggestedDomainName: data.suggestedDomainName,
          suggestedGroupId: data.suggestedGroupId,
          suggestedGroupName: data.suggestedGroupName,
          topicName: newTopicName,
        });
      } else if (data.error) {
        alert(`Error: ${data.error}`);
      } else {
        const newId = String(data.newTopic.id);
        setNewTopicName('');
        setNewTopicModal({ topic: data.newTopic, newId });
        authFetch(`${process.env.REACT_APP_API_URL}/api/topics/${selectedGroup}`)
          .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
          .then((refreshed) => setTopics(refreshed))
          .catch((err) => console.error('Error fetching topics:', err));
      }
    } catch (error) {
      if (error.name === 'AbortError') alert('Request timed out after 30 seconds. Please try again.');
      else { console.error('Error adding new topic:', error); alert('Something went wrong adding the new topic.' + error); }
    } finally {
      clearTimeout(timeoutId);
      setAddingTopic(false);
    }
  };

  const handleForceCreateTopic = async () => {
    if (!pendingTopicPayload) return;
    setAddingTopic(true);
    setSimilarTopicsWarning(null);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/topics/chatgpt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pendingTopicPayload, force: true }),
      });
      const data = await response.json();
      if (data.error) {
        alert(`Error: ${data.error}`);
      } else {
        const newId = String(data.newTopic.id);
        setNewTopicName('');
        setNewTopicModal({ topic: data.newTopic, newId });
        authFetch(`${process.env.REACT_APP_API_URL}/api/topics/${selectedGroup}`)
          .then(res => res.json())
          .then(refreshed => setTopics(refreshed))
          .catch(err => console.error('Error fetching topics:', err));
      }
    } catch (err) {
      alert('Something went wrong: ' + err.message);
    } finally {
      setAddingTopic(false);
      setPendingTopicPayload(null);
    }
  };

  const handleSearchNavigate = (result) => {
    localStorage.setItem('studySelectedDomain', String(result.domain_id));
    localStorage.setItem('studySelectedGroup',  String(result.group_id));
    localStorage.setItem('studySelectedTopic',  String(result.id));
    if (selectedDomain !== String(result.domain_id)) {
      setGroups([]); setSelectedGroup('');
      setTopics([]); setSelectedTopic('');
      setSelectedDomain(String(result.domain_id));
    } else if (selectedGroup !== String(result.group_id)) {
      setTopics([]); setSelectedTopic('');
      setTopicsLoading(true);
      setSelectedGroup(String(result.group_id));
    } else {
      setSelectedTopic(String(result.id));
    }
  };

  const handleMoveToSuggestedGroup = async () => {
    if (!wrongGroupWarning) return;
    const { suggestedDomainId, suggestedDomainName, suggestedGroupId, suggestedGroupName, topicName } = wrongGroupWarning;
    setWrongGroupWarning(null);
    setPendingTopicPayload(null);

    // Navigate UI to the correct domain/group
    setSelectedDomain(String(suggestedDomainId));
    localStorage.setItem('studySelectedDomain', String(suggestedDomainId));
    localStorage.setItem('studySelectedGroup', String(suggestedGroupId));
    // The selectedDomain useEffect will load groups and auto-select suggestedGroupId from localStorage

    // Submit directly to the suggested group (force: true — AI confirmed this placement)
    setAddingTopic(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/topics/chatgpt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topicName, groupId: suggestedGroupId, groupName: suggestedGroupName, domainName: suggestedDomainName, force: true }),
      });
      const data = await response.json();
      if (data.error) {
        alert(`Error: ${data.error}`);
      } else {
        const newId = String(data.newTopic.id);
        setNewTopicName('');
        setNewTopicModal({ topic: data.newTopic, newId });
        authFetch(`${process.env.REACT_APP_API_URL}/api/topics/${suggestedGroupId}`)
          .then(res => res.json())
          .then(refreshed => setTopics(refreshed))
          .catch(err => console.error('Error fetching topics:', err));
      }
    } catch (err) {
      alert('Something went wrong: ' + err.message);
    } finally {
      setAddingTopic(false);
    }
  };

  const handleResetGoal = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/goal/${selectedTopic}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!data.error) { setGoalDate(''); setStartDate(''); setExtendOpen(false); }
    } catch (error) {
      console.error('Error resetting goal:', error);
    }
  };

  const handleExtend = async () => {
    if (!extendDate) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/goal/${selectedTopic}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goal_date: extendDate }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setGoalDate(extendDate);
      setExtendOpen(false);
      setExtendDate('');
    } catch (error) {
      console.error('Error extending goal:', error);
    }
  };

  const selectedTopicObj = topics.find((t) => t.id === parseInt(selectedTopic, 10));
  const daysRemaining = goalDate ? Math.floor((new Date(goalDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  const goalPace = (() => {
    if (!goalDate || !startDate || questions.length === 0) return null;
    // Parse as local midnight to avoid UTC offset shifting the date by a day
    const parseLocal = (str) => {
      const [y, m, d] = str.split('-').map(Number);
      return new Date(y, m - 1, d);
    };
    const start = parseLocal(startDate);
    const goal = parseLocal(goalDate);
    const today = (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); })();
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const totalDays = Math.max(1, Math.round((goal - start) / MS_PER_DAY));
    const daysElapsed = Math.max(0, Math.round((today - start) / MS_PER_DAY));
    const timePct = Math.min(daysElapsed / totalDays, 1);
    const currentConfidence = questions.reduce((sum, q) => sum + (q.last_confidence || 0), 0);
    const targetConfidence = questions.length * 3;
    const confidencePct = targetConfidence > 0 ? Math.min(currentConfidence / targetConfidence, 1) : 0;
    const delta = confidencePct - timePct;
    const status = delta >= 0.05 ? 'Ahead of pace' : delta >= -0.05 ? 'On track' : 'Behind pace';
    const statusClass = delta >= 0.05 ? 'ahead' : delta >= -0.05 ? 'ontrack' : 'behind';
    return { timePct, confidencePct, status, statusClass, currentConfidence, targetConfidence };
  })();

  return (
    <div className="study-page">
      <div className={`study-layout${sidebarCollapsed ? ' study-layout--collapsed' : ''}`}>

        {sidebarOpen && (
          <div className="study-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ── Sidebar ── */}
        <aside className={`study-sidebar${sidebarOpen ? ' study-sidebar--open' : ''}`}>
          <button className="study-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">✕</button>
          <button
            className="study-sidebar-toggle"
            onClick={handleToggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '›' : '‹'}
          </button>

          {/* Search */}
          <div className="study-sidebar-section">
            <TopicSearch onNavigate={handleSearchNavigate} />
          </div>

          {/* Tier 1 — Domain */}
          <div className="study-sidebar-section">
            <p className="study-sidebar-label">Domain</p>
            <SelectDomain
              domains={domains}
              selectedDomain={selectedDomain}
              onChange={handleDomainChange}
            />
          </div>

          {/* Admin: Add Domain */}
          {isAdmin && (
            <div className="study-sidebar-section">
              <p className="study-sidebar-label">Add Domain</p>
              <div className="study-add-topic">
                <input
                  className="study-add-topic-input"
                  type="text"
                  placeholder="Domain name…"
                  value={newDomainName}
                  onChange={(e) => setNewDomainName(e.target.value)}
                  data-form-type="other"
                />
                <button className="study-btn-sm" onClick={handleAddNewDomain}>Add</button>
              </div>
            </div>
          )}

          {/* Tier 2 — Group */}
          {selectedDomain && !isRandomMode && (
            <div className="study-sidebar-section">
              <p className="study-sidebar-label">Group</p>
              <SelectGroup
                groups={groups}
                selectedGroup={selectedGroup}
                onChange={handleGroupChange}
              />
            </div>
          )}

          {/* Admin: Add Group */}
          {isAdmin && selectedDomain && !isRandomMode && (
            <div className="study-sidebar-section">
              <p className="study-sidebar-label">Add Group</p>
              <div className="study-add-topic">
                <input
                  className="study-add-topic-input"
                  type="text"
                  placeholder="Group name…"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  data-form-type="other"
                />
                <button className="study-btn-sm" onClick={handleAddNewGroup}>Add</button>
              </div>
            </div>
          )}

          {/* Tier 3 — Topic */}
          {selectedGroup && (
            <div className="study-sidebar-section">
              <p className="study-sidebar-label">Topic</p>
              {topicsLoading ? (
                <p className="study-sidebar-spinner">Loading…</p>
              ) : (
                <SelectTopic
                  topics={topics}
                  selectedTopic={selectedTopic}
                  onChange={handleTopicChange}
                />
              )}
            </div>
          )}

          {/* All users: Add Topic (AI) */}
          {selectedGroup && (
            <>
              <hr className="study-sidebar-divider" />
              <div className="study-sidebar-section">
                <p className="study-sidebar-label">Add Topic</p>
                <div className="study-add-topic">
                  <input
                    className="study-add-topic-input"
                    type="text"
                    placeholder="Topic name…"
                    value={newTopicName}
                    onChange={(e) => setNewTopicName(e.target.value)}
                    disabled={addingTopic}
                    data-form-type="other"
                  />
                  <button className="study-btn-sm" onClick={handleAddNewTopic} disabled={addingTopic}>
                    {addingTopic ? '…' : 'Add'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Topic info card */}
          {selectedTopicObj && (
            <>
              <hr className="study-sidebar-divider" />
              <div className="study-topic-card">
                <p className="study-topic-name">{selectedTopicObj.name}</p>
                <p className="study-topic-meta">
                  Vendor: <span>{selectedTopicObj.vendor || '—'}</span><br />
                  Est. exam questions: <span>{selectedTopicObj.expected_exam_questions || '—'}</span>
                  {selectedTopicObj.official_url && (
                    <><br />Official site: <a href={selectedTopicObj.official_url} target="_blank" rel="noreferrer">{selectedTopicObj.official_url}</a></>
                  )}
                </p>
              </div>
            </>
          )}

          {/* Goal section */}
          {selectedTopic && (
            <>
              <hr className="study-sidebar-divider" />
              {goalDate ? (
                <div className="study-goal-card">
                  <p className="study-goal-date">Goal: {goalDate}</p>
                  <p className="study-goal-days">
                    {daysRemaining >= 0 ? `${daysRemaining} days remaining` : `${Math.abs(daysRemaining)} days overdue`}
                  </p>
                  {goalPace && (
                    <div className="study-goal-pace">
                      <p className="study-pace-heading">Exam Readiness</p>
                      <div className="study-pace-row">
                        <span className="study-pace-label">Time</span>
                        <div className="study-pace-bar-track">
                          <div className="study-pace-bar-fill study-pace-bar-time" style={{ width: `${Math.round(goalPace.timePct * 100)}%` }} />
                        </div>
                        <span className="study-pace-pct">{Math.round(goalPace.timePct * 100)}%</span>
                      </div>
                      <div className="study-pace-row">
                        <span className="study-pace-label">Pace</span>
                        <div className="study-pace-bar-track">
                          <div className="study-pace-bar-fill study-pace-bar-confidence" style={{ width: `${Math.round(goalPace.confidencePct * 100)}%` }} />
                        </div>
                        <span className="study-pace-pct">{Math.round(goalPace.confidencePct * 100)}%</span>
                      </div>
                      <p className={`study-pace-status study-pace-status--${goalPace.statusClass}`}>{goalPace.status}</p>
                      <p className="study-pace-detail">{goalPace.currentConfidence} / {goalPace.targetConfidence} confidence pts</p>
                    </div>
                  )}
                  {extendOpen ? (
                    <div className="study-date-row" style={{ marginTop: '0.5rem' }}>
                      <input
                        className="study-date-input"
                        type="date"
                        value={extendDate}
                        onChange={e => setExtendDate(e.target.value)}
                        data-form-type="other"
                      />
                      <button className="study-btn-goal-submit" onClick={handleExtend} disabled={!extendDate}>Save</button>
                      <button className="study-flag-cancel" onClick={() => { setExtendOpen(false); setExtendDate(''); }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button className="study-btn-goal-submit" onClick={() => { setExtendDate(goalDate); setExtendOpen(true); }}>Extend</button>
                      <button className="study-goal-reset" onClick={handleResetGoal}>Reset</button>
                    </div>
                  )}
                </div>
              ) : (
                <AddGoal
                  topic={selectedTopic}
                  setGoalDate={setGoalDate}
                  setStartDate={setStartDate}
                  topicName={selectedTopicObj?.name}
                />
              )}
            </>
          )}

        </aside>

        {/* ── Main ── */}
        <main className="study-main">
          <button
            className="study-mobile-sidebar-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            ☰ Topics
          </button>
          <Link to="/faq" className="study-help-link">? Help</Link>
          <QuestionsList
            questions={questions}
            selectedTopic={selectedTopic}
            selectedDomain={selectedDomain}
            isRandomMode={isRandomMode}
            onGenerateQuestion={handleGenerateQuestions}
            generatingQuestion={loading}
            isAdmin={isAdmin}
            onBatchGenerate={handleBatchGenerate}
            batchGenerating={batchGenerating}
            batchProgress={batchProgress}
            goalPace={goalPace}
            questionsLoading={questionsLoading}
          />
        </main>

      </div>

      {similarTopicsWarning && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setSimilarTopicsWarning(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,1)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ background: 'var(--color-surface)', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '1.5rem', width: '480px', maxWidth: '95vw' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem' }}>
              {exactMatchWarning ? 'This Topic Already Exists' : 'Similar Topics Already Exist'}
            </div>
            {similarTopicsWarning.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--color-border)' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{t.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-subtle)' }}>
                    {t.domain_name} › {t.group_name}{t.similarity != null ? ` — ${Math.round(t.similarity * 100)}% similar` : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    localStorage.setItem('studySelectedGroup', String(t.group_id));
                    localStorage.setItem('studySelectedTopic', String(t.id));
                    setSelectedDomain(String(t.domain_id));
                    setSelectedGroup(String(t.group_id));
                    setSelectedTopic(String(t.id));
                    setSimilarTopicsWarning(null);
                    setPendingTopicPayload(null);
                    setExactMatchWarning(false);
                  }}
                  style={{ padding: '0.25rem 0.7rem', borderRadius: '6px', fontSize: '0.78rem', border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)', color: 'var(--color-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: '0.75rem' }}
                >
                  Take me there
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => { setSimilarTopicsWarning(null); setPendingTopicPayload(null); setExactMatchWarning(false); }} className="costs-range-btn">Cancel</button>
              {!exactMatchWarning && (
                <button
                  onClick={handleForceCreateTopic}
                  style={{ padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)', background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
                >
                  Create Anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {wrongGroupWarning && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setWrongGroupWarning(null); setPendingTopicPayload(null); } }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ background: 'var(--color-surface)', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '1.5rem', width: '480px', maxWidth: '95vw' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem' }}>Topic may be in the wrong place</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
              <strong>"{wrongGroupWarning.topicName}"</strong> looks like it belongs in{' '}
              <strong>{wrongGroupWarning.suggestedDomainName} › {wrongGroupWarning.suggestedGroupName}</strong>.
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setWrongGroupWarning(null); setPendingTopicPayload(null); }}
                className="costs-range-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleForceCreateTopic}
                className="costs-range-btn"
              >
                Add here anyway
              </button>
              <button
                onClick={handleMoveToSuggestedGroup}
                style={{ padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)', background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
              >
                Move to {wrongGroupWarning.suggestedGroupName}
              </button>
            </div>
          </div>
        </div>
      )}

      {newTopicModal && (
        <NewTopicModal
          topic={newTopicModal.topic}
          isAdmin={isAdmin}
          onClose={(updatedTopic) => {
            const { newId } = newTopicModal;
            setNewTopicModal(null);
            if (updatedTopic) {
              setTopics(prev => prev.map(t => String(t.id) === newId ? { ...t, ...updatedTopic } : t));
            }
            setSelectedTopic(newId);
            localStorage.setItem('studySelectedTopic', newId);
          }}
        />
      )}
    </div>
  );
}

export default Study;
